import { MAX_DOWNLOAD_COUNT } from '../../../constants/statics'
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import pluralize from 'pluralize'
import { Button, Header, Modal } from '@adc/ui-core'
import { CSVLink } from 'react-csv'
import { formatNumber } from '../../../utils/formatters'
import DataTableUtils from '../utils'

// statuses local to this process only
const STATUSES = {
  INITIALIZING: 'INITIALIZING',
  FETCHING: 'FETCHING',
  CONVERTING: 'CONVERTING',
  READY: 'READY',
  ERROR: 'ERROR',
}

const DownloadMenuItem = {
  key: 'export_csv_data',
  value: 'export_csv_data',
  text: 'Download All',
}

class DownloadDataModal extends Component {
  static propTypes = {
    modalOpen: PropTypes.bool.isRequired,
    closeModal: PropTypes.func.isRequired,
    columns: PropTypes.array.isRequired,
    totalCount: PropTypes.number,
    filename: PropTypes.string.isRequired,
    notify: PropTypes.func.isRequired,
    downloadData: PropTypes.object.isRequired,
    downloadActions: PropTypes.shape({
      fetchData: PropTypes.func.isRequired,
      abort: PropTypes.func.isRequired,
      reset: PropTypes.func.isRequired,
    }).isRequired,
    maxDownloadCount: PropTypes.number,
  }

  static defaultProps = {
    maxDownloadCount: MAX_DOWNLOAD_COUNT,
  }

  state = {
    status: STATUSES.INITIALIZING,
    statusText: 'Initializing...',
    csvData: null,
  }

  isTooManyRows = () => {
    const { totalCount, maxDownloadCount } = this.props // this is the grid's totalCount (not download)
    return totalCount > maxDownloadCount
  }

  componentDidUpdate(prevProps) {
    const { downloadData: prevDownloadData } = prevProps
    const {
      modalOpen,
      downloadData: { errorInfo = {}, isBatchSuccessful },
    } = this.props

    try {
      if (!prevProps.modalOpen && modalOpen) {
        this.fetchData()
      } else if (
        modalOpen &&
        _.isEmpty(prevDownloadData.errorInfo) &&
        !_.isEmpty(errorInfo)
      ) {
        this.handleError(`Error preparing file. ${errorInfo.statusText}.`)
      } else if (
        modalOpen &&
        !prevDownloadData.isBatchSuccessful &&
        isBatchSuccessful
      ) {
        // done with fetching!!!
        this.convertData()
      }
    } catch (error) {
      this.handleError(error)
    }
  }

  fetchData = () => {
    if (this.isTooManyRows()) {
      this.handleError(
        `The maximum number of rows you can download is ${formatNumber(
          this.props.maxDownloadCount
        )}.
        Please use the search filters to narrow down your results before downloading.`
      )
      return
    }

    this.setState({
      status: STATUSES.FETCHING,
    })

    this.props.downloadActions.fetchData()
  }

  convertData = () => {
    const {
      columns,
      downloadData: { content },
    } = this.props
    this.setState({ status: STATUSES.CONVERTING })
    const csvData = DataTableUtils.getCSVExportData(columns, content)

    this.setState({
      status: STATUSES.READY,
      csvData,
    })
  }

  handleError = errorMessage => {
    this.setState({
      status: STATUSES.ERROR,
      errorMessage,
    })
  }

  renderStatusText = () => {
    const {
      downloadData: { totalRetrieved, totalCount },
    } = this.props
    const { status, errorMessage } = this.state
    switch (status) {
      case STATUSES.INITIALIZING:
        return 'Initializing...'
      case STATUSES.FETCHING:
        return (
          `Fetching: ${formatNumber(totalRetrieved)} of ` +
          `${formatNumber(totalCount)} ${pluralize('row', totalCount)}...`
        )
      case STATUSES.CONVERTING:
        return 'Converting to CSV...'
      case STATUSES.READY:
        return `File is ready. ${formatNumber(totalRetrieved)} ${pluralize(
          'row',
          totalRetrieved
        )} retrieved.`
      case STATUSES.ERROR:
        return errorMessage
    }
  }

  closeModal = () => {
    const { status } = this.state
    const {
      downloadActions: { abort, reset },
      notify,
    } = this.props

    if (status !== STATUSES.READY && status !== STATUSES.ERROR) {
      notify('Download aborted by user')
      abort() // abort will also reset...
    } else {
      reset() // clear out whatever that has been fetched so far
    }

    this.props.closeModal()
  }

  render() {
    const { status, csvData } = this.state
    const { filename } = this.props
    const fileIsReady = status === STATUSES.READY
    const isError = status === STATUSES.ERROR

    return (
      <Modal
        closeOnDimmerClick={false}
        elmId='modal_download'
        size='tiny'
        open={this.props.modalOpen}
        onClose={this.closeModal}
        closeIcon={false}
        mobileTitleSkin='light'
      >
        <Header
          as='h2'
          content={DownloadMenuItem.text}
          data-elm-id='header_download'
        />
        <Modal.Content>
          <p data-elm-id='content_download'>{this.renderStatusText()}</p>
          {fileIsReady && (
            <CSVLink
              data-elm-id='download_link'
              data={csvData}
              filename={filename}
            >
              Click here to download file.
            </CSVLink>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            size='small'
            elmId='button_cancel_download'
            onClick={this.closeModal}
          >
            {fileIsReady || isError ? 'Close' : 'Cancel'}
          </Button>
        </Modal.Actions>
      </Modal>
    )
  }
}

export default DownloadDataModal
export { DownloadMenuItem }
